import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ImagePlus, Mic, Send, Volume2, VolumeX, Wand2 } from "lucide-react";
import { ARTI_ACCENT, ARTI_NAME, ARTI_ROLE, ARTI_TTS, ARTI_VOICE_KEY, artiAck } from "../lib/artiDesk";
import { cancelSpeech, speakText } from "../lib/speechPrompt";
import "./ArtiVoicePanel.css";

export function useArtiDeskVoice() {
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem(ARTI_VOICE_KEY) !== "off"; } catch { return true; }
  });
  const [line, setLine] = useState(() => artiAck("hello_chat"));
  const [speaking, setSpeaking] = useState(false);
  const voiceOnRef = useRef(voiceOn);
  voiceOnRef.current = voiceOn;

  useEffect(() => {
    try { localStorage.setItem(ARTI_VOICE_KEY, voiceOn ? "on" : "off"); } catch { /* ignore */ }
    if (!voiceOn) {
      cancelSpeech();
      setSpeaking(false);
    }
  }, [voiceOn]);

  useEffect(() => () => cancelSpeech(), []);

  const speak = useCallback((text) => {
    const phrase = String(text || "").trim();
    if (!phrase) return;
    setLine(phrase);
    if (!voiceOnRef.current) {
      setSpeaking(false);
      return;
    }
    const started = speakText(phrase, {
      ...ARTI_TTS,
      onstart: () => setSpeaking(true),
      onend: () => setSpeaking(false),
      onerror: () => setSpeaking(false),
    });
    if (!started) setSpeaking(false);
  }, []);

  return { voiceOn, setVoiceOn, line, speaking, speak, setLine };
}

export default function ArtiVoicePanel({
  talk,
  onTalkChange,
  messages = [],
  busy,
  busyKind,
  canGenerate,
  canEdit,
  canReference,
  micSupported,
  listening,
  speaking,
  voiceOn,
  onVoiceToggle,
  line,
  onMic,
  onSubmit,
  onGenerate,
  onEdit,
  onReference,
  onSendToPrompt,
  pendingPrompt,
  suggestGenerate,
  compact = false,
  bar = false,
}) {
  const threadRef = useRef(null);
  const [open, setOpen] = useState(false);
  const status = listening ? "listening" : speaking ? "speaking" : busy ? "thinking" : "idle";
  const statusLabel = listening
    ? "Listening"
    : speaking
      ? "Speaking"
      : busyKind === "chat"
        ? "Thinking"
        : busyKind === "edit"
          ? "Editing"
          : busyKind === "generate"
            ? "Generating"
            : "Ready";
  const thread = (messages || []).slice(-8);

  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [thread.length, line, busyKind, open]);

  const seenThread = useRef(thread.length);
  useEffect(() => {
    if (pendingPrompt) setOpen(true);
  }, [pendingPrompt]);
  useEffect(() => {
    if (thread.length > seenThread.current) setOpen(true);
    seenThread.current = thread.length;
  }, [thread.length]);

  const expanded = !bar || open;

  const actions = (
    <div className="arti-panel-actions">
      <button
        className={canEdit ? "btn" : "btn primary"}
        type="button"
        disabled={busy || !canGenerate}
        onClick={onGenerate}
        data-testid="forgelink-arti-generate"
      >
        <Wand2 size={13} /> Generate from design input
      </button>
      <button
        className={canEdit ? "btn primary" : "btn"}
        type="button"
        disabled={busy || !canEdit}
        onClick={onEdit}
        data-testid="forgelink-arti-continue"
      >
        <ImagePlus size={13} /> Continue editing
      </button>
      <button
        className="btn"
        type="button"
        disabled={busy || !canReference}
        onClick={onReference}
        data-testid="forgelink-arti-reference"
      >
        Use as reference
      </button>
    </div>
  );

  const threadBlock = (
    <div className="arti-panel-thread" data-testid="forgelink-arti-thread" ref={threadRef}>
      {thread.length === 0 ? (
        <p className="arti-panel-line" data-testid="forgelink-arti-line">{line}</p>
      ) : (
        thread.map((msg, index) => (
          <p
            key={`${msg.role}-${index}-${String(msg.content || "").slice(0, 12)}`}
            className={`arti-panel-msg is-${msg.role}`}
          >
            <strong>{msg.role === "assistant" ? ARTI_NAME : "You"}</strong>
            <span>{msg.content}</span>
          </p>
        ))
      )}
    </div>
  );

  const pendingBlock = pendingPrompt ? (
    <div className="arti-panel-pending" data-testid="forgelink-arti-pending-prompt">
      <small>Suggested design prompt</small>
      <p>{pendingPrompt}</p>
      <button className="btn primary" type="button" onClick={onSendToPrompt} disabled={busy}>
        Put in design input
      </button>
      {suggestGenerate ? (
        <em className="arti-panel-pending-hint">When you are ready, Generate from design input.</em>
      ) : null}
    </div>
  ) : null;

  return (
    <section
      className={`arti-panel is-${status}${compact ? " is-compact" : ""}${bar ? " is-bar" : ""}${expanded ? " is-open" : ""}`}
      style={{ "--arti-accent": ARTI_ACCENT }}
      data-testid="forgelink-arti-panel"
      aria-label={`${ARTI_NAME} conversation panel`}
    >
      {bar ? null : (
        <div className="arti-panel-id">
          <span className="arti-panel-mark" aria-hidden="true">A</span>
          <div>
            <small>FORGELINC · CREATIVE</small>
            <b>{ARTI_NAME}</b>
            <em>{ARTI_ROLE} · talk with me</em>
          </div>
          <span className="arti-panel-state" data-testid="forgelink-arti-state">{statusLabel}</span>
        </div>
      )}

      <form
        className="arti-panel-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.();
        }}
      >
        {bar ? <span className="arti-panel-mark" aria-hidden="true">A</span> : null}
        <input
          value={talk}
          onChange={(event) => onTalkChange?.(event.target.value)}
          placeholder="Talk with Arti"
          disabled={busy}
          data-testid="forgelink-arti-input"
          aria-label={`${ARTI_NAME} conversation`}
        />
        <button
          className={`arti-panel-icon${listening ? " is-on" : ""}`}
          type="button"
          onClick={onMic}
          disabled={busy}
          title={micSupported ? (listening ? "Stop listening" : "Speak to Arti") : "Voice input is not available in this browser"}
          aria-label={micSupported ? `${ARTI_NAME} voice chat` : "Voice input not supported"}
          data-testid="forgelink-arti-mic"
        >
          <Mic size={15} />
        </button>
        <button
          className={`arti-panel-icon${voiceOn ? " is-voice" : ""}`}
          type="button"
          onClick={onVoiceToggle}
          title={voiceOn ? "Voice replies on" : "Voice replies off"}
          aria-label={voiceOn ? "Turn Arti voice replies off" : "Turn Arti voice replies on"}
          data-testid="forgelink-arti-voice"
        >
          {voiceOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
        </button>
        <button className="arti-panel-icon" type="submit" disabled={busy} aria-label="Send to Arti" data-testid="forgelink-arti-send">
          <Send size={14} />
        </button>
        {bar ? (
          <button
            className={`arti-panel-icon${expanded ? " is-open" : ""}`}
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? "Hide Arti actions" : "Show Arti actions"}
            title={expanded ? "Hide actions" : "Generate, edit, and reference"}
            onClick={() => setOpen((value) => !value)}
            data-testid="forgelink-arti-more"
          >
            <ChevronDown size={15} />
          </button>
        ) : null}
        {bar ? <span className="arti-panel-state" data-testid="forgelink-arti-state">{statusLabel}</span> : null}
      </form>

      {expanded ? (
        <div className="arti-panel-more" data-testid="forgelink-arti-more-panel">
          {threadBlock}
          {pendingBlock}
          {actions}
        </div>
      ) : null}
    </section>
  );
}
